import {
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import cookie from 'cookie';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import { AuthContext } from '@src/app/contexts/Auth';
import { formItems } from '@src/app/formModels/eligibility/outgoings';
import { useTrackAnchorClicks } from '@src/app/hooks/trackMixpanelEventsHooks/useTrackAnchorClicks';
import { handleAuthResult } from '@src/shared/helpers/auth';
import { getValidationSchema } from '@src/shared/helpers/input';

import BackButton from '../../components/BackButton/BackButton';
import ProgressMenu from '../../components/Progress/ProgressMenu';
import SubHeading from '../../components/SubHeading/SubHeading';

import OutgoingsViewForm from './OutgoingsView.form';

export interface OutgoingsViewProps {
    className?: string;
    coApplicantName: string;
    outgoingsData: any;
    saveOutgoings: (data: any) => Promise<any>;
}

const validationSchema = getValidationSchema(formItems);

const OutgoingsView = forwardRef(
    (
        { coApplicantName, outgoingsData, saveOutgoings }: OutgoingsViewProps,
        ref,
    ) => {
        const formikRef = useRef<any>(null);

        const [httpError, setHttpError] = useState(null);
        const [errors, setErrors] = useState({});

        useTrackAnchorClicks();

        const navigate = useNavigate();

        const { setCurrentForm, applicationEmail, debtConUser } =
            useContext(ApplicationContext);

        const { loggedIn, register } = useContext(AuthContext);

        useEffect(() => {
            formikRef.current.values = {
                ...outgoingsData,
            };
        }, [outgoingsData]);

        useEffect(() => {
            setCurrentForm(undefined);
        }, [setCurrentForm]);

        useEffect(() => {
            if (formikRef.current) {
                setCurrentForm(formikRef.current);
            }
        }, [formikRef, setCurrentForm]);

        const cookies = cookie.parse(document.cookie);
        const appInfo = cookies.application
            ? JSON.parse(cookies.application)
            : {};

        const handleSubmit = async (values: any) => {
            setErrors({});
            setHttpError(null);

            if (!saveOutgoings) return;

            const data = await saveOutgoings(values);

            if (!data.success) {
                setHttpError(data.error);
                return;
            }

            if (loggedIn) {
                const path = debtConUser ? 'debt-con/intro' : 'wait';
                navigate(`/eligibility/${path}`);
            } else {
                const result = await register(
                    applicationEmail,
                    appInfo.applicationId,
                );

                handleAuthResult(
                    result,
                    { ...values, email: applicationEmail },
                    setErrors,
                    () => navigate('/eligibility'),
                );
            }
        };

        const saveForLater = async () => {
            const values = formikRef.current.values;

            const data = await saveOutgoings(values);
            if (!data.success) {
                setHttpError(data.error);
            }
        };

        useImperativeHandle(ref, () => ({
            saveMethod: saveForLater,
        }));

        return (
            <div className='flex flex-row justify-between'>
                <div className='w-full sm:w-1/2 lg:w-2/5 mt-20 sm:mt-0 sm:mr-10'>
                    <div className='hidden lg:block mb-2'>
                        <BackButton />
                    </div>
                    <p className='text-2xl lg:text-4xl font-sora-bold tracking-tighter'>
                        Your spending
                    </p>
                    {coApplicantName && (
                        <SubHeading
                            text={`Please provide combined spending for you and ${coApplicantName}`}
                        />
                    )}
                    <div className='relative mb-15'>
                        <Formik
                            enableReinitialize
                            initialValues={{
                                ...outgoingsData,
                            }}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            innerRef={(ref) => (formikRef.current = ref)}
                        >
                            {(formikProps) => {
                                return (
                                    <Form>
                                        <OutgoingsViewForm
                                            formikProps={formikProps}
                                            formItems={formItems}
                                            errors={errors}
                                            httpError={httpError}
                                            coApplicantName={coApplicantName}
                                        />
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
                <ProgressMenu />
            </div>
        );
    },
);

export default OutgoingsView;
